import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  School,
  Psychology,
  Science,
  Code,
  Business,
  Palette,
  Translate,
  History,
  Engineering,
  MusicNote,
  SportsEsports,
  Gavel,
  TrendingUp,
  ExpandMore,
  CheckCircle,
  Book,
  Lightbulb,
  Timeline
} from '@mui/icons-material';
import MenuItem from '@mui/material/MenuItem';

const StudentPlanning = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [planningData, setPlanningData] = useState(null);
  
  const [studentInfo, setStudentInfo] = useState({
    grade: '',
    school: '',
    interests: [],
    careerGoals: []
  });

  const grades = ['9th Grade', '10th Grade', '11th Grade', '12th Grade'];

  const interestOptions = [
    { label: 'Psychology & Mental Health', icon: <Psychology />, color: '#ffffff', bgColor: '#d32f2f' },
    { label: 'Computer Science & Technology', icon: <Code />, color: '#ffffff', bgColor: '#1976d2' },
    { label: 'Biology & Medicine', icon: <Science />, color: '#ffffff', bgColor: '#388e3c' },
    { label: 'Business & Economics', icon: <Business />, color: '#ffffff', bgColor: '#f57c00' },
    { label: 'Arts & Design', icon: <Palette />, color: '#ffffff', bgColor: '#7b1fa2' },
    { label: 'Languages & Literature', icon: <Translate />, color: '#ffffff', bgColor: '#5d4037' },
    { label: 'History & Social Studies', icon: <History />, color: '#ffffff', bgColor: '#455a64' },
    { label: 'Mathematics & Engineering', icon: <Engineering />, color: '#ffffff', bgColor: '#303f9f' },
    { label: 'Music & Performing Arts', icon: <MusicNote />, color: '#ffffff', bgColor: '#c62828' },
    { label: 'Sports & Athletics', icon: <SportsEsports />, color: '#ffffff', bgColor: '#0277bd' },
    { label: 'Law & Politics', icon: <Gavel />, color: '#ffffff', bgColor: '#558b2f' }
  ];

  const careerGoals = [
    'Healthcare Professional', 'Software Engineer', 'Business Leader', 'Artist/Designer',
    'Teacher/Educator', 'Scientist/Researcher', 'Lawyer', 'Entrepreneur',
    'Public Service', 'Creative Professional', 'Technical Specialist', 'Other'
  ];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInterestToggle = (interest) => {
    setStudentInfo(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleCareerToggle = (career) => {
    setStudentInfo(prev => ({
      ...prev,
      careerGoals: prev.careerGoals.includes(career)
        ? prev.careerGoals.filter(c => c !== career)
        : [...prev.careerGoals, career]
    }));
  };

  const generatePlanningData = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockData = {
        courseRecommendations: [],
        yearPlanner: {},
        skillDevelopment: []
      };

      const { grade, interests } = studentInfo;
      const currentGradeIndex = grades.indexOf(grade);

      // Psychology & Mental Health path
      if (interests.includes('Psychology & Mental Health')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Psychology',
            description: 'Advanced study of human behavior and mental processes',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Psychology foundation', 'Critical thinking skills', 'Research methods'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for psychology, counseling, social work, and law careers'
          },
          {
            name: 'Introduction to Psychology',
            description: 'Basic understanding of psychological concepts and theories',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Psychology foundation', 'Understanding human behavior', 'Communication skills'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Useful for any career involving human interaction'
          },
          {
            name: 'Sociology',
            description: 'Study of human society and social behavior',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Social awareness', 'Cultural understanding', 'Analytical thinking'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Valuable for social work, education, and public service careers'
          },
          {
            name: 'Child Development',
            description: 'Study of physical, cognitive, and social development from birth to adolescence',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Child psychology', 'Development understanding', 'Communication skills'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for education, childcare, and family services careers'
          },
          {
            name: 'Abnormal Psychology',
            description: 'Study of psychological disorders and mental health conditions',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Mental health awareness', 'Clinical understanding', 'Empathy development'],
            prerequisites: 'Psychology I or AP Psychology',
            gradeLevel: '12th',
            careerRelevance: 'Direct preparation for clinical psychology and mental health careers'
          }
        );
      }

      // Computer Science & Technology path
      if (interests.includes('Computer Science & Technology')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Computer Science A',
            description: 'Advanced Java programming and software development principles',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Programming skills', 'Problem solving', 'Algorithm thinking'],
            prerequisites: 'Algebra I',
            gradeLevel: '10th-12th',
            careerRelevance: 'Essential for software engineering, data science, and IT careers'
          },
          {
            name: 'AP Computer Science Principles',
            description: 'Broad introduction to computer science concepts and computational thinking',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Computational thinking', 'Digital literacy', 'Creativity'],
            prerequisites: 'None',
            gradeLevel: '9th-12th',
            careerRelevance: 'Foundation for all technology-related careers'
          },
          {
            name: 'Introduction to Programming',
            description: 'Learn basic programming concepts using Python or JavaScript',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Programming fundamentals', 'Logical thinking', 'Problem solving'],
            prerequisites: 'None',
            gradeLevel: '9th-12th',
            careerRelevance: 'Valuable for any career requiring analytical thinking'
          },
          {
            name: 'Web Development',
            description: 'Learn HTML, CSS, and JavaScript for web applications',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Web development skills', 'Design thinking', 'User experience'],
            prerequisites: 'Basic computer skills',
            gradeLevel: '10th-12th',
            careerRelevance: 'Useful for web development, digital marketing, and entrepreneurship'
          },
          {
            name: 'Data Science & Analytics',
            description: 'Introduction to data analysis, statistics, and visualization',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Data analysis skills', 'Statistical thinking', 'Visualization techniques'],
            prerequisites: 'Algebra II',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for data science, business analytics, and research careers'
          },
          {
            name: 'Cybersecurity',
            description: 'Learn about computer security, ethical hacking, and digital protection',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Security awareness', 'Ethical hacking skills', 'Digital safety'],
            prerequisites: 'Computer Science I',
            gradeLevel: '11th-12th',
            careerRelevance: 'Growing field with high demand for security professionals'
          }
        );
      }

      // Biology & Medicine path
      if (interests.includes('Biology & Medicine')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Biology',
            description: 'Advanced study of biological systems, evolution, and cellular processes',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Medical school prep', 'Scientific thinking', 'Lab skills'],
            prerequisites: 'Biology I, Chemistry I',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for medicine, research, and healthcare careers'
          },
          {
            name: 'AP Chemistry',
            description: 'Advanced study of chemical principles and laboratory techniques',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Chemistry foundation', 'Analytical skills', 'Lab techniques'],
            prerequisites: 'Chemistry I, Algebra II',
            gradeLevel: '11th-12th',
            careerRelevance: 'Required for medicine, pharmacy, and chemical engineering'
          },
          {
            name: 'Anatomy & Physiology',
            description: 'Study of human body structure and function',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Medical knowledge', 'Body systems understanding', 'Healthcare foundation'],
            prerequisites: 'Biology I',
            gradeLevel: '11th-12th',
            careerRelevance: 'Directly applicable to healthcare and medical careers'
          },
          {
            name: 'Environmental Science',
            description: 'Study of environmental systems and human impact',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Environmental awareness', 'Scientific research', 'Policy understanding'],
            prerequisites: 'Biology I',
            gradeLevel: '10th-12th',
            careerRelevance: 'Valuable for environmental science, public health, and policy careers'
          },
          {
            name: 'AP Physics',
            description: 'Advanced study of physical principles and mathematical modeling',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Physics foundation', 'Mathematical modeling', 'Problem solving'],
            prerequisites: 'Algebra II, Trigonometry',
            gradeLevel: '11th-12th',
            careerRelevance: 'Required for engineering, physics, and some medical specialties'
          },
          {
            name: 'Medical Terminology',
            description: 'Learn medical language and healthcare communication',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Medical vocabulary', 'Healthcare communication', 'Professional language'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Essential for all healthcare and medical careers'
          }
        );
      }

      // Business & Economics path
      if (interests.includes('Business & Economics')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Microeconomics',
            description: 'Study of individual economic decision-making and market structures',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Economic thinking', 'Market understanding', 'Decision making'],
            prerequisites: 'Algebra I',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for business, finance, and economics careers'
          },
          {
            name: 'AP Macroeconomics',
            description: 'Study of national and global economic systems',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Economic systems', 'Policy understanding', 'Global perspective'],
            prerequisites: 'Algebra I',
            gradeLevel: '11th-12th',
            careerRelevance: 'Valuable for business, government, and international relations'
          },
          {
            name: 'Business Management',
            description: 'Introduction to business principles and management practices',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Business fundamentals', 'Leadership skills', 'Organizational understanding'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Foundation for business and entrepreneurship careers'
          },
          {
            name: 'Personal Finance',
            description: 'Learn about budgeting, investing, and financial planning',
            difficulty: 'Beginner',
            credits: 0.5,
            benefits: ['Financial literacy', 'Life skills', 'Investment knowledge'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential life skill for all careers'
          },
          {
            name: 'Marketing & Advertising',
            description: 'Study of consumer behavior and promotional strategies',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Marketing skills', 'Consumer psychology', 'Communication strategies'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Valuable for marketing, sales, and business development'
          },
          {
            name: 'Entrepreneurship',
            description: 'Learn about starting and managing your own business',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Business planning', 'Innovation skills', 'Risk management'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for entrepreneurs and business owners'
          }
        );
      }

      // Arts & Design path
      if (interests.includes('Arts & Design')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Studio Art',
            description: 'Advanced art portfolio development and creative expression',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Portfolio development', 'Creative skills', 'Artistic expression'],
            prerequisites: 'Art fundamentals',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for art, design, and creative careers'
          },
          {
            name: 'Digital Design',
            description: 'Learn digital art tools and graphic design principles',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Digital skills', 'Design thinking', 'Software proficiency', 'Creativity'],
            prerequisites: 'Basic computer skills',
            gradeLevel: '10th-12th',
            careerRelevance: 'Valuable for graphic design, marketing, and digital media'
          },
          {
            name: 'Photography',
            description: 'Learn photographic techniques and visual storytelling',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Visual skills', 'Storytelling', 'Technical proficiency', 'Creativity'],
            prerequisites: 'None',
            gradeLevel: '9th-12th',
            careerRelevance: 'Useful for media, marketing, and creative careers'
          },
          {
            name: 'Architecture & Design',
            description: 'Introduction to architectural principles and design thinking',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Design thinking', 'Spatial reasoning', 'Technical drawing', 'Creativity'],
            prerequisites: 'Geometry',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for architecture, interior design, and urban planning'
          },
          {
            name: 'Animation & Motion Graphics',
            description: 'Learn animation techniques and digital motion design',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Animation skills', 'Digital storytelling', 'Software proficiency'],
            prerequisites: 'Digital Design or Art Fundamentals',
            gradeLevel: '11th-12th',
            careerRelevance: 'Valuable for film, gaming, and digital media industries'
          }
        );
      }

      // Languages & Literature path
      if (interests.includes('Languages & Literature')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP English Literature',
            description: 'Advanced study of literary works and critical analysis',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Literary analysis', 'Critical thinking', 'Writing skills'],
            prerequisites: 'English 11',
            gradeLevel: '12th',
            careerRelevance: 'Essential for literature, writing, and humanities careers'
          },
          {
            name: 'AP English Language',
            description: 'Advanced study of rhetoric and composition',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Writing skills', 'Rhetorical analysis', 'Communication'],
            prerequisites: 'English 10',
            gradeLevel: '11th',
            careerRelevance: 'Valuable for all careers requiring strong communication skills'
          },
          {
            name: 'Creative Writing',
            description: 'Develop creative writing skills and artistic expression',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Creative expression', 'Writing skills', 'Imagination', 'Storytelling'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Useful for writing, publishing, and creative careers'
          },
          {
            name: 'AP Spanish/French/German',
            description: 'Advanced study of foreign language and culture',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Language proficiency', 'Cultural understanding', 'Global perspective'],
            prerequisites: '3-4 years of language study',
            gradeLevel: '11th-12th',
            careerRelevance: 'Valuable for international business, diplomacy, and global careers'
          },
          {
            name: 'Journalism',
            description: 'Learn news writing, reporting, and media production',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Writing skills', 'Research methods', 'Media literacy', 'Communication'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Essential for journalism, media, and communications careers'
          }
        );
      }

      // History & Social Studies path
      if (interests.includes('History & Social Studies')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP World History',
            description: 'Advanced study of global historical developments and themes',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Global perspective', 'Historical analysis', 'Cultural understanding'],
            prerequisites: 'None',
            gradeLevel: '10th',
            careerRelevance: 'Valuable for international relations, education, and cultural careers'
          },
          {
            name: 'AP US History',
            description: 'Advanced study of American history and historical interpretation',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'American history', 'Historical thinking', 'Document analysis'],
            prerequisites: 'None',
            gradeLevel: '11th',
            careerRelevance: 'Essential for law, politics, and American studies careers'
          },
          {
            name: 'AP European History',
            description: 'Study of European history from Renaissance to present',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'European history', 'Cultural understanding', 'Historical analysis'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Valuable for international relations and European studies'
          },
          {
            name: 'Geography',
            description: 'Study of physical and human geography',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Spatial awareness', 'Cultural understanding', 'Environmental knowledge'],
            prerequisites: 'None',
            gradeLevel: '9th-12th',
            careerRelevance: 'Useful for environmental science, urban planning, and international careers'
          },
          {
            name: 'Cultural Anthropology',
            description: 'Study of human cultures and societies',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Cultural understanding', 'Cross-cultural communication', 'Social analysis'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Valuable for international relations, social work, and cultural careers'
          }
        );
      }

      // Mathematics & Engineering path
      if (interests.includes('Mathematics & Engineering')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Calculus AB',
            description: 'Advanced study of differential and integral calculus',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Mathematical thinking', 'Problem solving', 'Engineering foundation'],
            prerequisites: 'Pre-Calculus',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for engineering, physics, and advanced mathematics careers'
          },
          {
            name: 'AP Calculus BC',
            description: 'Advanced calculus including series and parametric equations',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Advanced calculus', 'Mathematical rigor', 'Engineering prep'],
            prerequisites: 'AP Calculus AB or strong Pre-Calculus',
            gradeLevel: '12th',
            careerRelevance: 'Required for advanced engineering and mathematics careers'
          },
          {
            name: 'AP Statistics',
            description: 'Study of statistical methods and data analysis',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Statistical thinking', 'Data analysis', 'Research methods'],
            prerequisites: 'Algebra II',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for data science, research, and business analytics'
          },
          {
            name: 'Engineering Design',
            description: 'Introduction to engineering principles and design process',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Engineering thinking', 'Design process', 'Problem solving', 'Technical skills'],
            prerequisites: 'Physics I, Algebra II',
            gradeLevel: '11th-12th',
            careerRelevance: 'Direct preparation for engineering careers'
          },
          {
            name: 'Robotics & Automation',
            description: 'Learn about robotics, programming, and automation systems',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Robotics skills', 'Programming', 'Systems thinking', 'Innovation'],
            prerequisites: 'Computer Science I, Physics I',
            gradeLevel: '11th-12th',
            careerRelevance: 'Growing field with high demand for automation specialists'
          },
          {
            name: 'Linear Algebra',
            description: 'Study of vectors, matrices, and linear transformations',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Advanced mathematics', 'Abstract thinking', 'Computational skills'],
            prerequisites: 'AP Calculus AB',
            gradeLevel: '12th',
            careerRelevance: 'Essential for advanced engineering, computer science, and mathematics'
          }
        );
      }

      // Music & Performing Arts path
      if (interests.includes('Music & Performing Arts')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Music Theory',
            description: 'Advanced study of musical structure, harmony, and composition',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Musical understanding', 'Composition skills', 'Analytical thinking'],
            prerequisites: 'Music fundamentals',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for music composition, performance, and education careers'
          },
          {
            name: 'Music Production',
            description: 'Learn digital music production and recording techniques',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Production skills', 'Technology integration', 'Creative expression'],
            prerequisites: 'Basic music knowledge',
            gradeLevel: '10th-12th',
            careerRelevance: 'Valuable for music industry, audio engineering, and media careers'
          },
          {
            name: 'Theater Arts',
            description: 'Study of acting, directing, and theatrical production',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Performance skills', 'Public speaking', 'Creative expression', 'Collaboration'],
            prerequisites: 'None',
            gradeLevel: '9th-12th',
            careerRelevance: 'Useful for acting, directing, and entertainment careers'
          },
          {
            name: 'Dance & Choreography',
            description: 'Study of dance techniques and choreographic principles',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Physical fitness', 'Artistic expression', 'Coordination', 'Creativity'],
            prerequisites: 'None',
            gradeLevel: '9th-12th',
            careerRelevance: 'Essential for dance, choreography, and performing arts careers'
          },
          {
            name: 'Film & Video Production',
            description: 'Learn filmmaking techniques and video production',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Visual storytelling', 'Technical skills', 'Creative expression'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Valuable for film, media, and entertainment industries'
          }
        );
      }

      // Law & Politics path
      if (interests.includes('Law & Politics')) {
        mockData.courseRecommendations.push(
          {
            name: 'AP Government & Politics',
            description: 'Study of American political systems and government structures',
            difficulty: 'Advanced',
            credits: 1,
            benefits: ['College credit', 'Political understanding', 'Civic knowledge', 'Critical analysis'],
            prerequisites: 'US History',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for law, politics, and public service careers'
          },
          {
            name: 'Constitutional Law',
            description: 'Study of constitutional principles and legal interpretation',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Legal thinking', 'Constitutional understanding', 'Analytical skills'],
            prerequisites: 'US History',
            gradeLevel: '12th',
            careerRelevance: 'Direct preparation for law school and legal careers'
          },
          {
            name: 'Speech & Debate',
            description: 'Develop public speaking and argumentation skills',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Public speaking', 'Critical thinking', 'Persuasion skills', 'Confidence'],
            prerequisites: 'None',
            gradeLevel: '10th-12th',
            careerRelevance: 'Essential for law, politics, and leadership careers'
          },
          {
            name: 'International Relations',
            description: 'Study of global politics and international systems',
            difficulty: 'Advanced',
            credits: 0.5,
            benefits: ['Global perspective', 'Political analysis', 'Cultural understanding'],
            prerequisites: 'World History',
            gradeLevel: '11th-12th',
            careerRelevance: 'Valuable for diplomacy, international law, and global careers'
          },
          {
            name: 'Criminal Justice',
            description: 'Study of legal system and criminal justice processes',
            difficulty: 'Intermediate',
            credits: 0.5,
            benefits: ['Legal understanding', 'Justice system knowledge', 'Analytical thinking'],
            prerequisites: 'None',
            gradeLevel: '11th-12th',
            careerRelevance: 'Essential for law enforcement, legal, and criminal justice careers'
          }
        );
      }

      // Generate year planner (only for current and future grades)
      const futureGrades = grades.slice(currentGradeIndex);
      mockData.yearPlanner = {};

      futureGrades.forEach((grade, index) => {
        const gradeIndex = currentGradeIndex + index;
        
        switch (gradeIndex) {
          case 0: // 9th Grade
            mockData.yearPlanner[grade] = {
              core: ['Algebra I', 'Biology', 'English 9', 'World History'],
              electives: ['Introduction to Computer Science', 'Art Fundamentals', 'Physical Education'],
              apCourses: ['AP Human Geography (if available)'],
              recommendations: [
                'Focus on building strong GPA foundation - aim for 3.5+',
                'Join academic clubs and explore interests (Science Club, Math Team, etc.)',
                'Start developing study habits and time management skills',
                'Consider taking first AP course if academically ready',
                'Begin building relationships with teachers for future recommendations',
                'Start a reading habit to improve vocabulary and comprehension',
                'Participate in school activities to build leadership skills',
                'Begin exploring potential career paths through research and shadowing'
              ],
              collegePrep: [
                'Research potential colleges and career paths',
                'Start keeping track of extracurricular activities and achievements',
                'Consider summer programs or camps in areas of interest',
                'Begin building a strong academic foundation',
                'Start thinking about potential majors and career goals',
                'Consider taking the PSAT 8/9 for practice',
                'Begin developing strong writing and communication skills'
              ],
              milestones: [
                'Complete Algebra I with B+ or better',
                'Maintain 3.5+ GPA',
                'Join at least 2 clubs or activities',
                'Complete 20+ hours of community service',
                'Start building a portfolio of achievements'
              ]
            };
            break;
            
          case 1: // 10th Grade
            mockData.yearPlanner[grade] = {
              core: ['Geometry', 'Chemistry', 'English 10', 'US History'],
              electives: ['AP Computer Science Principles', 'Psychology', 'Foreign Language'],
              apCourses: ['AP Computer Science Principles', 'AP World History (if available)'],
              recommendations: [
                'Take first AP course to demonstrate academic rigor',
                'Begin leadership roles in clubs or organizations',
                'Start community service and volunteer work',
                'Focus on maintaining strong GPA (3.5+)',
                'Consider taking SAT Subject Tests in strong subjects',
                'Deepen involvement in areas of interest',
                'Start preparing for standardized tests',
                'Begin researching colleges and universities',
                'Consider summer internships or research opportunities',
                'Develop strong relationships with teachers and counselors'
              ],
              collegePrep: [
                'Take PSAT/NMSQT for National Merit consideration',
                'Research and visit colleges of interest',
                'Start building relationships with counselors',
                'Consider summer internships or research opportunities',
                'Begin preparing for SAT/ACT',
                'Research scholarship opportunities',
                'Start thinking about college applications',
                'Consider taking SAT Subject Tests in strong subjects'
              ],
              milestones: [
                'Complete Geometry with B+ or better',
                'Take first AP course',
                'Achieve leadership position in at least one activity',
                'Complete 40+ hours of community service',
                'Take PSAT/NMSQT',
                'Maintain 3.6+ GPA',
                'Begin building college application profile'
              ]
            };
            break;
            
          case 2: // 11th Grade
            mockData.yearPlanner[grade] = {
              core: ['Algebra II', 'Physics', 'English 11', 'Government'],
              electives: ['AP Psychology', 'AP Biology', 'AP Computer Science A', 'Economics'],
              apCourses: ['AP Psychology', 'AP Biology', 'AP Computer Science A', 'AP Language & Composition'],
              recommendations: [
                'Take multiple AP courses to demonstrate academic rigor',
                'Register for and take SAT/ACT',
                'Deepen involvement in extracurricular activities',
                'Seek leadership positions in clubs or organizations',
                'Start working on college applications and essays',
                'Focus on maintaining excellent GPA (3.7+)',
                'Begin serious college research and visits',
                'Consider summer programs at colleges of interest',
                'Start building relationships with potential recommenders',
                'Begin preparing for college interviews'
              ],
              collegePrep: [
                'Take SAT/ACT multiple times for best scores',
                'Visit colleges and attend information sessions',
                'Request letters of recommendation from teachers',
                'Begin drafting college application essays',
                'Research scholarships and financial aid opportunities',
                'Consider early decision/early action applications',
                'Prepare for college interviews',
                'Complete FAFSA preparation',
                'Research and apply for summer programs'
              ],
              milestones: [
                'Complete Algebra II with B+ or better',
                'Take 3+ AP courses',
                'Achieve leadership position in multiple activities',
                'Complete 60+ hours of community service',
                'Take SAT/ACT',
                'Maintain 3.7+ GPA',
                'Begin college applications',
                'Secure strong letters of recommendation'
              ]
            };
            break;
            
          case 3: // 12th Grade
            mockData.yearPlanner[grade] = {
              core: ['Pre-Calculus', 'English 12', 'Economics'],
              electives: ['AP Calculus', 'AP Literature', 'AP Statistics', 'Advanced Science'],
              apCourses: ['AP Calculus AB/BC', 'AP Literature', 'AP Statistics', 'AP Environmental Science'],
              recommendations: [
                'Complete college applications by deadlines',
                'Maintain strong GPA and course rigor',
                'Continue leadership and extracurricular involvement',
                'Prepare for college transition and independence',
                'Consider taking additional AP courses for college credit',
                'Focus on finishing strong academically',
                'Begin preparing for college-level coursework',
                'Develop independent living skills',
                'Consider gap year opportunities if desired',
                'Maintain relationships with mentors and teachers'
              ],
              collegePrep: [
                'Submit all college applications by deadlines',
                'Complete FAFSA and financial aid applications',
                'Make final college decision by May 1st',
                'Attend admitted student events and orientations',
                'Prepare for college-level coursework and independence',
                'Research and apply for additional scholarships',
                'Complete housing applications',
                'Attend college orientation programs',
                'Prepare for academic and social transition'
              ],
              milestones: [
                'Complete Pre-Calculus with B+ or better',
                'Take 4+ AP courses',
                'Submit all college applications',
                'Complete FAFSA',
                'Make final college decision',
                'Maintain 3.8+ GPA',
                'Complete 80+ hours of community service',
                'Achieve significant leadership accomplishments'
              ]
            };
            break;
          default:
            // Handle any unexpected grade index
            break;
        }
      });

      setPlanningData(mockData);
    } catch (error) {
      console.error('Error generating planning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#fff', fontWeight: 900, fontSize: '2rem', textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Current Grade"
                  value={studentInfo.grade}
                  onChange={(e) => setStudentInfo(prev => ({ ...prev, grade: e.target.value }))}
                  margin="normal"
                  required
                  sx={{
                    '& .MuiInputBase-root': { color: '#fff', fontWeight: 700 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                    '& .MuiSvgIcon-root': { color: '#fff' },
                  }}
                >
                  <MenuItem value="">
                    <em>Select your grade</em>
                  </MenuItem>
                  {grades.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      {grade}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={<span style={{ color: '#fff', fontWeight: 700 }}>School Name</span>}
                  InputLabelProps={{ style: { color: '#fff', fontWeight: 700 } }}
                  value={studentInfo.school}
                  onChange={(e) => setStudentInfo(prev => ({ ...prev, school: e.target.value }))}
                  margin="normal"
                  placeholder="Enter your school name"
                  required
                  sx={{
                    '& .MuiInputBase-root': { color: '#fff', fontWeight: 700 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              What interests you most?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select all that apply to help us personalize your course recommendations
            </Typography>
            <Grid container spacing={2}>
              {interestOptions.map((interest) => {
                const selected = studentInfo.interests.includes(interest.label);
                return (
                  <Grid item xs={12} sm={6} md={4} key={interest.label}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selected ? 2 : 1,
                        borderColor: selected ? interest.bgColor : '#e0e0e0',
                        backgroundColor: selected ? interest.bgColor : '#f9f9f9',
                        color: selected ? '#fff' : '#222',
                        boxShadow: selected ? 6 : 1,
                        '&:hover': {
                          borderColor: interest.bgColor,
                          backgroundColor: selected ? interest.bgColor : '#ececec',
                          color: selected ? '#fff' : '#111',
                          boxShadow: 6
                        },
                        transition: 'all 0.3s ease',
                        minHeight: 120
                      }}
                      onClick={() => handleInterestToggle(interest.label)}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Box sx={{ color: selected ? '#fff' : '#888', mb: 1, display: 'flex', justifyContent: 'center', fontSize: 32 }}>
                          {interest.icon}
                        </Box>
                        <Typography
                          variant="body1"
                          fontWeight={selected ? 700 : 500}
                          sx={{
                            color: selected ? '#fff' : '#222',
                            fontWeight: selected ? 700 : 500,
                            fontSize: 18
                          }}
                        >
                          {interest.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Career Goals
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              What careers are you considering? This helps us suggest relevant courses and activities.
            </Typography>
            <Grid container spacing={2}>
              {careerGoals.map((career) => (
                <Grid item xs={12} sm={6} md={4} key={career}>
                  <Chip
                    label={career}
                    onClick={() => handleCareerToggle(career)}
                    variant={studentInfo.careerGoals.includes(career) ? 'filled' : 'outlined'}
                    sx={{
                      m: 0.5,
                      background: studentInfo.careerGoals.includes(career) ? '#2196f3' : '#fff',
                      color: studentInfo.careerGoals.includes(career) ? '#fff' : '#111',
                      fontWeight: 900,
                      fontSize: '1rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      border: '2.5px solid #111',
                      transition: 'all 0.2s',
                      '&.MuiChip-filled': {
                        background: '#2196f3',
                        color: '#fff',
                        fontWeight: 900,
                        border: '2.5px solid #111',
                      },
                      '&.MuiChip-outlined': {
                        background: '#fff',
                        color: '#111',
                        fontWeight: 900,
                        border: '2.5px solid #111',
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const renderPlanningResults = () => {
    if (!planningData) return null;

    return (
      <Box sx={{ mt: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '100vh', p: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          textAlign: 'center', 
          mb: 4,
          background: 'none',
          color: '#fff',
          fontWeight: 900,
          letterSpacing: 1,
          textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
        }}>
          Your Personalized Academic Plan
        </Typography>

        {/* Course Recommendations */}
        <Accordion defaultExpanded sx={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: 'none', mb: 3, borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ background: 'linear-gradient(90deg, #667eea 0%, #2196f3 100%)', color: '#fff' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: '#fff', fontWeight: 900, textShadow: '1px 1px 2px rgba(0,0,0,0.2)' }}>
              <Book sx={{ mr: 1, color: '#fff' }} />
              Recommended Courses
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ background: 'rgba(255,255,255,0.95)', p: 3 }}>
            <Grid container spacing={3}>
              {planningData.courseRecommendations.map((course, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card sx={{ 
                    height: '100%', 
                    background: 'rgba(255,255,255,0.98)', 
                    color: '#222', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.10)', 
                    borderRadius: 3, 
                    border: '2.5px solid #667eea',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                      border: '2.5px solid #2196f3'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#2196f3', fontWeight: 900, fontSize: '1.2rem', textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
                        {course.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, color: '#222', fontWeight: 700, fontSize: '1rem' }}>
                        {course.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={course.difficulty} size="small" sx={{ background: studentInfo.careerGoals.includes(course.difficulty) ? '#2196f3' : '#fff', color: studentInfo.careerGoals.includes(course.difficulty) ? '#fff' : '#111', fontWeight: 900, fontSize: '1rem', border: '2px solid #2196f3' }} />
                        <Chip label={`${course.credits} credit`} size="small" sx={{ background: '#fff', color: '#111', fontWeight: 900, fontSize: '1rem', border: '2px solid #667eea' }} />
                        <Chip label={course.gradeLevel} size="small" sx={{ background: '#fff', color: '#111', fontWeight: 900, fontSize: '1rem', border: '2px solid #764ba2' }} />
                      </Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: '#667eea', fontWeight: 900, fontSize: '1.1rem' }}>
                        Benefits:
                      </Typography>
                      <List dense sx={{ background: 'rgba(102,126,234,0.08)', borderRadius: 2, p: 1 }}>
                        {course.benefits.map((benefit, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <CheckCircle fontSize="small" sx={{ color: '#2196f3' }} />
                            </ListItemIcon>
                            <ListItemText primary={benefit} primaryTypographyProps={{ style: { color: '#222', fontSize: '1rem', fontWeight: 700 } }} />
                          </ListItem>
                        ))}
                      </List>
                      <Alert severity="info" sx={{ mt: 2, background: 'rgba(102,126,234,0.10)', color: '#222', border: '2px solid #2196f3', borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ color: '#222', fontWeight: 800 }}>
                          <strong>Career Relevance:</strong> {course.careerRelevance}
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Year Planner */}
        <Accordion sx={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 8px 32px rgba(0,0,0,0.08)', border: 'none', borderRadius: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ background: 'linear-gradient(90deg, #667eea 0%, #2196f3 100%)', color: '#fff' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', color: '#fff', fontWeight: 800, textShadow: '1px 1px 2px rgba(0,0,0,0.2)' }}>
              <Timeline sx={{ mr: 1, color: '#fff' }} />
              Academic Year Planner
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ background: 'rgba(255,255,255,0.95)', p: 3 }}>
            <Grid container spacing={3}>
              {Object.entries(planningData.yearPlanner).map(([grade, data]) => (
                <Grid item xs={12} key={grade}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.98)', 
                    color: '#222', 
                    boxShadow: '0 4px 20px rgba(0,0,0,0.10)', 
                    borderRadius: 3, 
                    border: '2.5px solid #667eea',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                      border: '2.5px solid #2196f3'
                    },
                    transition: 'all 0.3s ease'
                  }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" gutterBottom sx={{ color: '#2196f3', mb: 3, fontWeight: 900 }}>
                        {grade}
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#667eea', fontWeight: 800, fontSize: '1.1rem' }}>
                            Core Courses
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, p: 2, background: 'rgba(102,126,234,0.08)', borderRadius: 2 }}>
                            {data.core.map((course, idx) => (
                              <Chip key={idx} label={course} size="small" sx={{ background: '#fff', color: '#111', fontWeight: 700, border: '2px solid #667eea' }} />
                            ))}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#2196f3', fontWeight: 800, fontSize: '1.1rem' }}>
                            Electives
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, p: 2, background: 'rgba(33,150,243,0.08)', borderRadius: 2 }}>
                            {data.electives.map((course, idx) => (
                              <Chip key={idx} label={course} size="small" sx={{ background: '#fff', color: '#111', fontWeight: 700, border: '2px solid #2196f3' }} />
                            ))}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#764ba2', fontWeight: 800, fontSize: '1.1rem' }}>
                            AP Courses
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, p: 2, background: 'rgba(118,75,162,0.08)', borderRadius: 2 }}>
                            {data.apCourses.map((course, idx) => (
                              <Chip key={idx} label={course} size="small" sx={{ background: '#fff', color: '#111', fontWeight: 700, border: '2px solid #764ba2' }} />
                            ))}
                          </Box>
                        </Grid>
                      </Grid>
                      <Divider sx={{ my: 3, background: '#667eea', height: 3, borderRadius: 2 }} />
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#667eea', fontWeight: 800, fontSize: '1.1rem' }}>
                            Academic Recommendations
                          </Typography>
                          <List dense sx={{ background: 'rgba(102,126,234,0.08)', borderRadius: 2, p: 1 }}>
                            {data.recommendations.map((rec, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <Lightbulb fontSize="small" sx={{ color: '#2196f3' }} />
                                </ListItemIcon>
                                <ListItemText primary={rec} primaryTypographyProps={{ style: { color: '#222', fontSize: '0.9rem', fontWeight: 600 } }} />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#2196f3', fontWeight: 800, fontSize: '1.1rem' }}>
                            College Preparation
                          </Typography>
                          <List dense sx={{ background: 'rgba(33,150,243,0.08)', borderRadius: 2, p: 1 }}>
                            {data.collegePrep.map((prep, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <School fontSize="small" sx={{ color: '#2196f3' }} />
                                </ListItemIcon>
                                <ListItemText primary={prep} primaryTypographyProps={{ style: { color: '#222', fontSize: '0.9rem', fontWeight: 600 } }} />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#764ba2', fontWeight: 800, fontSize: '1.1rem' }}>
                            Key Milestones
                          </Typography>
                          <List dense sx={{ background: 'rgba(118,75,162,0.08)', borderRadius: 2, p: 1 }}>
                            {data.milestones.map((milestone, idx) => (
                              <ListItem key={idx} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <TrendingUp fontSize="small" sx={{ color: '#764ba2' }} />
                                </ListItemIcon>
                                <ListItemText primary={milestone} primaryTypographyProps={{ style: { color: '#222', fontSize: '0.9rem', fontWeight: 600 } }} />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ 
        textAlign: 'center', 
        mb: 4,
        background: 'linear-gradient(45deg, #667eea, #764ba2)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 'bold'
      }}>
        High School Academic Planning
      </Typography>

      {!planningData ? (
        <>
          <Stepper activeStep={activeStep} orientation="vertical">
            {['Basic Information', 'Interests', 'Career Goals'].map((label, index) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
                <StepContent>
                  {renderStepContent(index)}
                  <Box sx={{ mb: 2 }}>
                    <div>
                      <Button
                        variant="contained"
                        onClick={index === 2 ? generatePlanningData : handleNext}
                        sx={{ mr: 1 }}
                        disabled={loading}
                      >
                        {index === 2 ? 'Generate My Plan' : 'Continue'}
                        {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
                      </Button>
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                      >
                        Back
                      </Button>
                    </div>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </>
      ) : (
        renderPlanningResults()
      )}
    </Box>
  );
};

export default StudentPlanning;